import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Chip,
  Alert,
  Box,
  IconButton,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { formatUtcToLocal } from "../../../services/dateUtils";
import {
  formatAttemptErrorMessage,
  formatClientDisplayName,
  formatKycStatusChip,
  formatPoiStatusChip,
} from "./kycStatusUtils";

const DetailField = ({ label, value, secondary }) => (
  <Grid item xs={12} sm={6}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body1" sx={{ wordBreak: "break-word" }}>
      {value ?? "-"}
    </Typography>
    {secondary ? (
      <Typography variant="caption" color="text.secondary" display="block">
        {secondary}
      </Typography>
    ) : null}
  </Grid>
);

const formatAttemptStatusChip = (status) => formatPoiStatusChip(status);

const KycVerificationDetailModal = ({ open, row, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (!row) return null;

  const statusChip = formatKycStatusChip(row.status);
  const poiChip = formatPoiStatusChip(row.poi_status);
  const displayName = formatClientDisplayName(row);
  const companyName = String(row.company_name || "").trim();
  const fullName = `${row.first_name || ""} ${row.last_name || ""}`.trim();
  const attempts = Array.isArray(row.attempts) ? row.attempts : [];
  const retriesLeft =
    row.retries_left === 0 || row.retries_left
      ? String(row.retries_left)
      : "-";
  const latestError =
    row.declined_reason ||
    formatAttemptErrorMessage(attempts[attempts.length - 1]);
  const hasErrorState =
    Boolean(latestError && latestError !== "-") ||
    ["declined", "rejected", "input_error", "invalid"].includes(
      String(row.poi_status || "").toLowerCase()
    );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      fullScreen={isMobile}
      aria-labelledby="kyc-detail-modal-title"
    >
      <DialogTitle
        id="kyc-detail-modal-title"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pr: 1,
        }}
      >
        <Box>
          <Typography variant="h6" component="div">
            {displayName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            KYC verification details
          </Typography>
        </Box>
        <IconButton aria-label="close" onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {hasErrorState && (
          <Alert
            severity={row.status === "rejected" ? "error" : "warning"}
            sx={{ mb: 2 }}
          >
            <Typography variant="subtitle2" gutterBottom>
              Latest error
            </Typography>
            <Typography variant="body2">
              {latestError && latestError !== "-"
                ? latestError
                : poiChip?.label ?? "-"}
            </Typography>
          </Alert>
        )}

        <Box sx={{ mb: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
          {statusChip ? (
            <Chip
              size="small"
              label={`Status: ${statusChip.label}`}
              sx={statusChip.sx}
            />
          ) : null}
          {poiChip ? (
            <Chip
              size="small"
              label={`POI: ${poiChip.label}`}
              sx={poiChip.sx}
            />
          ) : null}
          {row.retries_left === 0 || row.retries_left ? (
            <Chip
              size="small"
              variant="outlined"
              color={row.retries_left === 0 ? "error" : "default"}
              label={`Retries left: ${row.retries_left}`}
            />
          ) : null}
        </Box>

        <Grid container spacing={2}>
          <DetailField
            label="Client"
            value={displayName}
            secondary={
              row.client_info_id != null
                ? `Client ID: ${row.client_info_id}`
                : undefined
            }
          />
          {companyName ? (
            <DetailField label="Company" value={companyName} />
          ) : null}
          {fullName ? <DetailField label="Contact name" value={fullName} /> : null}
          <DetailField
            label="TRIA Account"
            value={row.provider_account_key}
          />
          <DetailField label="Email" value={row.email} />
          <DetailField label="Provider" value={row.provider_code} />
          <DetailField label="POI Status" value={poiChip?.label ?? "-"} />
          <DetailField label="Retries left" value={retriesLeft} />
          <DetailField label="Attempts recorded" value={attempts.length} />
        </Grid>

        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Verification attempts
          </Typography>
          {attempts.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No attempt history recorded.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Error / reason</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {attempts.map((attempt, index) => {
                    const attemptChip = formatAttemptStatusChip(attempt.status);
                    return (
                      <TableRow
                        key={attempt.id || `attempt-${index}`}
                        hover
                      >
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {formatUtcToLocal(attempt.created_at) || "-"}
                        </TableCell>
                        <TableCell>
                          {attemptChip ? (
                            <Chip
                              size="small"
                              label={attemptChip.label}
                              sx={attemptChip.sx}
                            />
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell sx={{ wordBreak: "break-word" }}>
                          {formatAttemptErrorMessage(attempt)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained" color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default KycVerificationDetailModal;
